// src/services/alertService.js
import api from './apiClient.js'

export const alertService = {
    // ==================== GESTION DES ALERTES ====================

    /**
     * Récupérer toutes les alertes avec filtres
     */
    async getAllAlerts(params = {}) {
        try {
            const response = await api.get('/alerts', {
                params
            })
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    },

    /**
     * Récupérer une alerte par ID
     */
    async getAlertById(id) {
        try {
            const response = await api.get(`/alerts/${id}`)
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    },

    /**
     * Obtenir les statistiques des alertes
     */
    async getStatistics() {
        try {
            const response = await api.get('/alerts/statistics')
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    },

    /**
     * Marquer des alertes comme lues
     */
    async markAsRead(alertIds = []) {
        try {
            const response = await api.post('/alerts/mark-as-read', {
                alertIds
            })
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    },

    /**
     * Marquer toutes les alertes comme lues
     */
    async markAllAsRead() {
        try {
            const response = await api.post('/alerts/mark-all-as-read')
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    },

    /**
     * Supprimer une alerte
     */
    async deleteAlert(id) {
        try {
            const response = await api.delete(`/alerts/${id}`)
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    },

    // ==================== VÉRIFICATIONS MANUELLES ====================

    /**
     * Vérifier les alertes de stock
     */
    async checkStockAlerts() {
        try {
            const response = await api.post('/alerts/check-stock')
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    },

    /**
     * Vérifier les alertes d'expiration
     */
    async checkExpirationAlerts() {
        try {
            const response = await api.post('/alerts/check-expiration')
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    },

    /**
     * Vérifier les alertes d'équipement
     */
    async checkEquipmentAlerts() {
        try {
            const response = await api.post('/alerts/check-equipment')
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    },

    /**
     * Vérifier toutes les alertes
     */
    async checkAllAlerts() {
        try {
            const response = await api.post('/alerts/check-all')
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    },

    /**
     * Nettoyer les anciennes alertes
     */
    async cleanupOldAlerts() {
        try {
            const response = await api.post('/alerts/cleanup')
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    },

    // ==================== CONFIGURATION ====================

    /**
     * Obtenir la configuration des alertes
     */
    async getConfiguration() {
        try {
            const response = await api.get('/alerts/configuration')
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    },

    /**
     * Mettre à jour la configuration des alertes
     */
    async updateConfiguration(configData) {
        try {
            const response = await api.patch('/alerts/configuration', configData)
            return response.data
        } catch (error) {
            throw this.handleError(error)
        }
    },

    // ==================== UTILITAIRES ====================

    /**
     * Gestion des erreurs
     */
    handleError(error) {
        if (error.response) {
            const {
                status,
                data
            } = error.response

            switch (status) {
                case 400:
                    return new Error(data.message || 'Données invalides')
                case 401:
                    return new Error('Non autorisé')
                case 403:
                    return new Error('Accès refusé')
                case 404:
                    return new Error('Alerte non trouvée')
                case 500:
                    return new Error('Erreur interne du serveur')
                default:
                    return new Error(data.message || 'Une erreur est survenue')
            }
        } else if (error.request) {
            return new Error('Erreur de connexion au serveur')
        } else {
            return new Error(error.message || 'Une erreur inattendue est survenue')
        }
    },

    /**
     * Obtenir le libellé du type d'alerte
     */
    getTypeLabel(type) {
        const labels = {
            'STOCK_LOW': 'Stock bas',
            'STOCK_CRITICAL': 'Stock critique',
            'STOCK_OUT': 'Rupture de stock',
            'PRODUCT_EXPIRING_SOON': 'Expiration prochaine',
            'PRODUCT_EXPIRING_URGENT': 'Expiration urgente',
            'PRODUCT_EXPIRED': 'Produit expiré',
            'EQUIPMENT_MAINTENANCE_DUE': 'Maintenance à prévoir',
            'EQUIPMENT_MAINTENANCE_OVERDUE': 'Maintenance en retard',
            'EQUIPMENT_OUT_OF_ORDER': 'Équipement hors service',
            'ORDER_PENDING_APPROVAL': 'Commande en attente',
            'ORDER_DELAYED': 'Commande en retard',
            'ORDER_RECEIVED': 'Commande reçue',
            'TRANSFER_PENDING': 'Transfert en attente',
            'TRANSFER_APPROVED': 'Transfert approuvé',
            'TRANSFER_REJECTED': 'Transfert rejeté'
        }
        return labels[type] || type
    },

    /**
     * Obtenir la sévérité du type pour PrimeVue
     */
    getTypeSeverity(type) {
        const severities = {
            'STOCK_LOW': 'warning',
            'STOCK_CRITICAL': 'danger',
            'STOCK_OUT': 'danger',
            'PRODUCT_EXPIRING_SOON': 'warning',
            'PRODUCT_EXPIRING_URGENT': 'danger',
            'PRODUCT_EXPIRED': 'danger',
            'EQUIPMENT_MAINTENANCE_DUE': 'info',
            'EQUIPMENT_MAINTENANCE_OVERDUE': 'danger',
            'EQUIPMENT_OUT_OF_ORDER': 'danger',
            'ORDER_PENDING_APPROVAL': 'info',
            'ORDER_DELAYED': 'warning',
            'ORDER_RECEIVED': 'success',
            'TRANSFER_PENDING': 'info',
            'TRANSFER_APPROVED': 'success',
            'TRANSFER_REJECTED': 'danger'
        }
        return severities[type] || 'info'
    },

    /**
     * Obtenir le libellé de la priorité
     */
    getPriorityLabel(priority) {
        const labels = {
            'CRITICAL': 'Critique',
            'HIGH': 'Haute',
            'MEDIUM': 'Moyenne',
            'LOW': 'Faible',
            'INFO': 'Information'
        }
        return labels[priority] || priority
    },

    /**
     * Obtenir la sévérité de la priorité pour PrimeVue
     */
    getPrioritySeverity(priority) {
        const severities = {
            'CRITICAL': 'danger',
            'HIGH': 'warning',
            'MEDIUM': 'info',
            'LOW': 'secondary',
            'INFO': 'secondary'
        }
        return severities[priority] || 'info'
    },

    /**
     * Obtenir l'icône selon le type d'alerte
     */
    getTypeIcon(type) {
        const icons = {
            'STOCK_LOW': 'pi pi-exclamation-triangle',
            'STOCK_CRITICAL': 'pi pi-exclamation-circle',
            'STOCK_OUT': 'pi pi-times-circle',
            'PRODUCT_EXPIRING_SOON': 'pi pi-clock',
            'PRODUCT_EXPIRING_URGENT': 'pi pi-clock',
            'PRODUCT_EXPIRED': 'pi pi-ban',
            'EQUIPMENT_MAINTENANCE_DUE': 'pi pi-wrench',
            'EQUIPMENT_MAINTENANCE_OVERDUE': 'pi pi-exclamation-triangle',
            'EQUIPMENT_OUT_OF_ORDER': 'pi pi-times-circle',
            'ORDER_PENDING_APPROVAL': 'pi pi-hourglass',
            'ORDER_DELAYED': 'pi pi-exclamation-triangle',
            'ORDER_RECEIVED': 'pi pi-check-circle',
            'TRANSFER_PENDING': 'pi pi-arrow-right-arrow-left',
            'TRANSFER_APPROVED': 'pi pi-check',
            'TRANSFER_REJECTED': 'pi pi-times'
        }
        return icons[type] || 'pi pi-info-circle'
    },

    /**
     * Obtenir les options de type
     */
    getTypeOptions() {
        return [{
                label: 'Stock bas',
                value: 'STOCK_LOW'
            },
            {
                label: 'Stock critique',
                value: 'STOCK_CRITICAL'
            },
            {
                label: 'Rupture de stock',
                value: 'STOCK_OUT'
            },
            {
                label: 'Expiration prochaine',
                value: 'PRODUCT_EXPIRING_SOON'
            },
            {
                label: 'Expiration urgente',
                value: 'PRODUCT_EXPIRING_URGENT'
            },
            {
                label: 'Produit expiré',
                value: 'PRODUCT_EXPIRED'
            },
            {
                label: 'Maintenance à prévoir',
                value: 'EQUIPMENT_MAINTENANCE_DUE'
            },
            {
                label: 'Maintenance en retard',
                value: 'EQUIPMENT_MAINTENANCE_OVERDUE'
            },
            {
                label: 'Équipement hors service',
                value: 'EQUIPMENT_OUT_OF_ORDER'
            },
            {
                label: 'Commande en attente',
                value: 'ORDER_PENDING_APPROVAL'
            },
            {
                label: 'Commande en retard',
                value: 'ORDER_DELAYED'
            },
            {
                label: 'Commande reçue',
                value: 'ORDER_RECEIVED'
            },
            {
                label: 'Transfert en attente',
                value: 'TRANSFER_PENDING'
            },
            {
                label: 'Transfert approuvé',
                value: 'TRANSFER_APPROVED'
            },
            {
                label: 'Transfert rejeté',
                value: 'TRANSFER_REJECTED'
            }
        ]
    },

    /**
     * Obtenir les options de priorité
     */
    getPriorityOptions() {
        return [{
                label: 'Critique',
                value: 'CRITICAL'
            },
            {
                label: 'Haute',
                value: 'HIGH'
            },
            {
                label: 'Moyenne',
                value: 'MEDIUM'
            },
            {
                label: 'Faible',
                value: 'LOW'
            },
            {
                label: 'Information',
                value: 'INFO'
            }
        ]
    },

    /**
     * Formater une date
     */
    formatDate(dateString) {
        if (!dateString) return '-'
        return new Date(dateString).toLocaleDateString('fr-FR')
    },

    /**
     * Formater une date et heure
     */
    formatDateTime(dateString) {
        if (!dateString) return '-'
        return new Date(dateString).toLocaleString('fr-FR')
    },

    /**
     * Formater pour affichage
     */
    formatAlertForDisplay(alert) {
        return {
            ...alert,
            typeLabel: this.getTypeLabel(alert.type),
            typeSeverity: this.getTypeSeverity(alert.type),
            typeIcon: this.getTypeIcon(alert.type),
            priorityLabel: this.getPriorityLabel(alert.priority),
            prioritySeverity: this.getPrioritySeverity(alert.priority),
            formattedCreatedAt: this.formatDateTime(alert.createdAt),
            formattedReadAt: this.formatDateTime(alert.readAt),
            formattedExpiresAt: this.formatDateTime(alert.expiresAt)
        }
    }
}

export default alertService